/* Counterparties — the Excalibur counterparty list (/sales/counterparties). Spec/03 archetype A,
   reduced the way `orders/List.js` is: no selection or bulk bar (nothing is
   edited from the list — the card at `/sales/counterparties/:id` is where the seven card fields
   change). Sorting is server-side and only on the two columns the endpoint orders
   (`ordering_fields = ("name", "remaining_funds")` on picsell-api's `CounterpartyViewSet`) — an
   arrow on any other column could only reorder the loaded page, which reads as a sort and is not
   one. So the type / category / lifecycle / status / supervisor columns carry no sorter.

   Field shapes from `GET excalibur/counterparties/`: `department` is a nested `{id, name}` (id a
   UUID) or null, `merchandisers` an array of `{id, first_name, last_name, photo}`, `photo_url` the
   counterparty's own picture, `remaining_funds` a signed JSON NUMBER (the API does not coerce
   decimals to strings; `money()` in `tabParts.js` renders it), `external_id` the record's code in
   the system that feeds ours, `client_type` /
   `client_category` a dictionary value `{id, label}` or null, `assigned_supervisor` a
   `{id, first_name, last_name, photo}` or null, `lifecycle_status` a TextChoices KEY or null
   (`lifecycle_is_manual` beside it). The row also carries
   `sales_channel`, `start_cooperation_at` and the sync fields; the card shows those, the list
   does not.

   Seven columns show by default (name, type, category, lifecycle, supervisor, balance and the row
   menu, after the logo), sized to fit a 1366px screen with no horizontal scroll; department,
   address and responsible start hidden and come back through the Columns panel (see
   `DEFAULT_HIDDEN`). The lifecycle is a column of filled status pills (`./status`). There is no
   activity any more — the API dropped `is_active` from counterparties.

   Seven filters are the counterparty's own: the department, three dictionary pickers (client
   type, category, sales channel — one endpoint, narrowed by `kind`), the assigned supervisor, and
   the lifecycle. The supervisor picker asks for deactivated supervisors too: one still sits on
   counterparties, and a filter that cannot name it cannot find them. ⚠ The dictionary endpoint
   lists live values only, so a DELETED value that still sits on counterparties cannot be picked
   here any more (a link carrying its id still filters by it).
   The lifecycle is the list's `<Segmented>` — the stage is the working-set split here, as the
   status is on `/sales/orders`. It is a LIST on the endpoint and in the state: the `Segmented` is
   only a view of it (none = "All", one = that segment, several — which only a link can carry —
   highlight nothing and are spelled out as chips instead). An `is_active=` left in an old link is
   simply not read, like `responsible=` below, so it never reaches the request.
   There is no "responsible" (merchandiser) filter any more; a `responsible=` left in an old link
   is simply not read. The column is still there, in the Columns panel.

   Five of the filters — `networks`, `points`, `groups`, `county`, `locality` — mean "ONE of the
   counterparty's points satisfies all of them" on the server, not five independent conditions.
   So they sit in their own block of the filter card, titled and explained, and their chips carry
   a "Points ·" prefix; each chip is still removable on its own. The block's header says in a few
   words that these work through the linked points; its body keeps the exact rule. It carries no count of
   active filters — the chips already list them, and a number beside a filter is banned app-wide
   (spec/03). */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { Avatar, Button, Card, Checkbox, Collapse, Dropdown, Empty, Flex, Form, Input, Segmented, Table, Tag, Tooltip, Typography } from "antd";
import {
  ApartmentOutlined,
  BankOutlined,
  ShareAltOutlined,
  ClusterOutlined,
  CompassOutlined,
  ExportOutlined,
  GroupOutlined,
  PushpinOutlined,
  RollbackOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  TableOutlined,
  TagOutlined,
  TagsOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import EntitySelect from "../../component/EntitySelect";
import FilterChips, { useLabelCache } from "../../component/FilterChips";
import FilterGrid, { FilterCell } from "../../component/FilterGrid";
import { PersonCell, RowActions, TableFoot, actionsColumn, dash, fullName, initialsOf, textOf, useIsWide, useTableScrollY } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, ORDERS_PAGE, PHOTO_REPORT } from "../../config/lang";
import { getAbortable } from "../../util/action";
import { translate } from "../../util/util";

import { CfHeaderNames, CustomFieldControls, FILTERABLE, cfChips, cfParams, hasCf, parseCf, sanitizeCf } from "./customFieldFilters";
import { LIFECYCLE_STATUSES, lifecycleTags } from "./status";
import { money } from "./tabParts";
import "./counterparties.scss";

/* The backend's `StandardResultsSetPagination` defaults to 25 — same list as `orders/List.js`. */
const PAGE_SIZES = [10, 25, 50, 100];
const DEFAULT_SIZE = PAGE_SIZES[1];

/* Every block of fields — the top one, the point block, the extra fields — is a `FilterGrid`: one
   column width for all of them, as many columns as fit, search first (there is no period here). A
   long chip ellipsizes inside its control rather than widening the column (`.lp-fgrid__cell`'s
   `min-width: 0`). The extra fields' `<Col>`s take no props, so each is a plain grid cell. */
const CF_COL = {};

/* Fixed picker scopes, as module constants — `EntitySelect` keys its fetch callbacks on the
   `extraQuery` identity. The endpoint lists live values only: see the header note. */
const KIND_QUERY = {
  client_type: { kind: "client_type" },
  client_category: { kind: "client_category" },
  sales_channel: { kind: "sales_channel" },
};

/** The ten entity filters, in panel order. `key` is the backend param AND the page's own URL
    param — one name for both, as `orders/List.js` does. `uuid` marks the ones whose ids are UUIDs
    (Excalibur departments, dictionary values) and must never be `Number()`-coerced when read back
    from the URL. `query` is a fixed `extraQuery` for the picker.

    Glyphs follow the nav rows for the same entities (spec/02#icons), not `fields/fieldIcons.js`:
    that map gives a network `ShopOutlined`, which is this page's own nav glyph — a shop meaning two
    things on one screen. The three dictionaries have no nav row, so their glyphs are this page's
    own choice (spec/02 is silent): a tag, tags, and the branching share glyph (a channel is a route to the client)
    — and not `AppstoreOutlined`, which already means a POINT category. */
const FILTERS = [
  /* The top block, in panel order: with the search box these five-plus-one fill a row of five and
     leave the sales channel on the second. The supervisor's label is the short column head, not
     the card's "Assigned supervisor" — a filter label sits over a ~200px field. */
  { key: "client_type", entity: "client_type", label: "k_client_type", Icon: TagOutlined, uuid: true, query: KIND_QUERY.client_type },
  { key: "client_category", entity: "client_category", label: "k_client_category", Icon: TagsOutlined, uuid: true, query: KIND_QUERY.client_category },
  { key: "department", entity: "department", label: "k_department", Icon: BankOutlined, uuid: true },
  { key: "assigned_supervisor", entity: "supervisor", label: "col_supervisor", Icon: TeamOutlined },
  { key: "sales_channel", entity: "sales_channel", label: "k_sales_channel", Icon: ShareAltOutlined, uuid: true, query: KIND_QUERY.sales_channel },
  // `point: true` — the server reads these five as one point's conditions (see the header note).
  { key: "networks", entity: "network", label: "f_network", Icon: ClusterOutlined, point: true },
  { key: "points", entity: "points", label: "f_point", Icon: PushpinOutlined, point: true },
  { key: "groups", entity: "point_groups", label: "f_point_group", Icon: GroupOutlined, point: true },
  { key: "county", entity: "county", label: "f_county", Icon: CompassOutlined, point: true },
  { key: "locality", entity: "locality", label: "f_locality", Icon: ApartmentOutlined, point: true },
];
/** The status filter: the lifecycle's TextChoices KEYS (`./status`), held in the same `filters`
    object as plain strings. `labelOf(value)` is the value's label key in `COUNTERPARTIES_PAGE`.
    `minChips` is how many values it takes before they are spelled out as chips: the
    `<Segmented>` already shows a single value, so a chip would only repeat it. */
const STATUS_FILTERS = [{ key: "lifecycle_status", label: "k_lifecycle", values: LIFECYCLE_STATUSES, labelOf: value => `lc_${value}`, minChips: 2 }];
/* Every multi-value param the request and the URL carry, entity and status alike. */
const PARAMS = [...FILTERS, ...STATUS_FILTERS].map(f => f.key);
/* A `Segmented` value that matches no option — what it holds when the URL carried several stages. */
const SEGMENT_MIXED = "mixed";
const OWN_FILTERS = FILTERS.filter(f => !f.point);
const POINT_FILTERS = FILTERS.filter(f => f.point);

/** Which key names each toggleable column in the Columns panel — `orders/List.js`'s shape. The
    columns shown by default come first, then the three that start hidden. The name (the only thing
    that identifies a row) and the row menu are absent, which is what keeps them mandatory. The
    panel has room for the full names, so it says "Client type" / "Remaining funds" where the
    100–158px column heads say "Type" / "Category" / "Balance". */
const COLUMN_LABEL = {
  client_type: "k_client_type",
  client_category: "k_client_category",
  lifecycle_status: "k_lifecycle",
  supervisor: "col_supervisor",
  remaining_funds: "k_remaining_funds",
  department: "k_department",
  address: "k_address",
  merchandisers: "k_responsible",
};
const TOGGLEABLE = Object.keys(COLUMN_LABEL);
/* What the Columns panel starts with switched OFF. The default columns add up to 1024px of the
   1026px a 1366px screen has room for (see the `scroll` note on the table); these three would add
   524px and bring the horizontal scrollbar back, so they are one click away instead. The
   department is still one filter and one click into the card away. The choice is not persisted,
   as in every other list — a reload returns to this. */
const DEFAULT_HIDDEN = ["department", "address", "merchandisers"];

/* Column key → the field the API orders on, `orders/List.js`'s shape; a column missing here is not
   sortable. Exactly the endpoint's `ordering_fields` — it ignores anything else silently, so a
   third entry would draw an arrow that does nothing. With no sort the request carries no
   `ordering` and the backend's own default (`name`, then `id`) applies; with one, the backend's
   `TieBreakingOrderingFilter` appends `-id`, so equal balances cannot repeat or drop rows across
   pages. `remaining_funds` is NOT NULL on the model, so there is no NULL placement to decide. */
const ORDERING = { name: "name", remaining_funds: "remaining_funds" };

const emptyFilters = () => Object.fromEntries(PARAMS.map(key => [key, []]));

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);
const joinIds = list =>
  (list || [])
    .map(idOf)
    .filter(v => v !== undefined && v !== null && v !== "")
    .join(",");

/** The request the table runs on — the one place the endpoint's filter shape is visible. Arrays
    go comma-joined (the endpoint's `InFilter`s), under the backend's own names. */
function buildQuery({ filters, search, sort, page, size, cf }) {
  const p = new URLSearchParams();
  p.set("page", String(page));
  p.set("page_size", String(size));
  if (search) p.set("search", search);
  PARAMS.forEach(key => {
    if (filters[key]?.length) p.set(key, joinIds(filters[key]));
  });
  /* Additional fields (`customFieldFilters.js`) — only once cleaned against their definitions. */
  cfParams(cf || {}).forEach(([name, value]) => p.set(name, value));
  if (sort) p.set("ordering", `${sort.dir === "descend" ? "-" : ""}${ORDERING[sort.key]}`);
  return p.toString();
}

/** URL ↔ state, so a filtered view can be shared, bookmarked, and survives Back from the orders
    list the row menu opens. Same encoding as `buildQuery` (comma-joined, backend names), with the
    `%2C` un-escaped for a readable link — see `orders/List.js`'s `stateToSearch` for why that is
    safe. Paging is written only when it differs from the default, so a fresh visit stays bare. */
function stateToSearch({ filters, search, sort, page, size, cf }) {
  const p = new URLSearchParams();
  if (search) p.set("search", search);
  PARAMS.forEach(key => {
    if (filters[key]?.length) p.set(key, joinIds(filters[key]));
  });
  cfParams(cf || {}).forEach(([name, value]) => p.set(name, value));
  if (page > 1) p.set("page", String(page));
  if (size !== DEFAULT_SIZE) p.set("size", String(size));
  // The COLUMN key, as `orders/List.js` writes it — `ORDERING` maps it to the API field.
  if (sort) p.set("sort", `${sort.dir === "descend" ? "-" : ""}${sort.key}`);
  return p.toString().replace(/%2C/g, ",");
}

/* What a well-formed id looks like. The endpoint answers a value that does not PARSE — a broken
   UUID, a non-number, an unknown status — with a 400 for the whole request, so one bad character in
   a pasted link would replace the table with "couldn't load" and a Retry that can never work. Such
   a value is dropped here instead, the way a `sort=` naming a column that does not sort is. A
   well-formed id that matches nothing is not our concern: the endpoint just returns no rows. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INT_RE = /^\d+$/;

function searchToState(search) {
  const p = new URLSearchParams(search);
  const size = Number(p.get("size"));
  const sort = p.get("sort");
  const listOf = key => [...new Set((p.get(key) || "").split(",").filter(Boolean))];
  /* Ids only — `EntitySelect` names them once it has fetched the option they belong to, and hands
     the names back through `onResolve` for the chips. */
  const filters = Object.fromEntries([
    ...FILTERS.map(({ key, uuid }) => [
      key,
      listOf(key)
        .filter(id => (uuid ? UUID_RE : INT_RE).test(id))
        .map(id => ({ id: uuid ? id : Number(id) })),
    ]),
    /* Statuses are their own keys — kept in the API's order, whatever order the link had. */
    ...STATUS_FILTERS.map(({ key, values }) => [key, values.filter(value => listOf(key).includes(value))]),
  ]);
  return {
    filters,
    /* `cf__…` as written — cleaned only once the field definitions are in (`sanitizeCf`). */
    cfRaw: parseCf(search),
    search: p.get("search") || "",
    page: Math.max(1, Number(p.get("page")) || 1),
    size: PAGE_SIZES.includes(size) ? size : DEFAULT_SIZE,
    // A pasted `sort=` naming a column that does not sort is dropped, not sent as a dead `ordering`.
    sort: sort && ORDERING[sort.replace(/^-/, "")] ? { key: sort.replace(/^-/, ""), dir: sort[0] === "-" ? "descend" : "ascend" } : null,
  };
}

/** First merchandiser as the house person cell, the rest as `+N` with every name on hover — the
    composed cell `points/List.js` draws for a point's supervisors. One `PersonCell` per person
    would make a row as tall as its team; a bare count would drop the photos the API sends. */
function MerchandisersCell({ people }) {
  const list = Array.isArray(people) ? people.filter(p => fullName(p)) : [];
  if (!list.length) return dash;
  const names = list.map(fullName).join(", ");
  return (
    <Flex align="center" gap={8} className="lp-main">
      <PersonCell user={list[0]} strong={false} />
      {list.length > 1 ? (
        <Tooltip title={names}>
          {/* `flex="none"`: the name beside it ellipsises, the count never does. */}
          <Flex flex="none">
            <Typography.Text type="secondary" aria-label={names}>
              <TeamOutlined /> +{list.length - 1}
            </Typography.Text>
          </Flex>
        </Tooltip>
      ) : null}
    </Flex>
  );
}

/** `search` without one parameter — an `assigned_supervisor=` a supervisor's own list must not read. */
const withoutParam = (search, key) => {
  const p = new URLSearchParams(search);
  p.delete(key);
  return p.toString();
};

const List = () => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const tp = key => translate(PHOTO_REPORT, key, lang);

  /* A supervisor sees only themselves and their team, so the "Супервайзер" filter is not theirs:
     not drawn, and an `assigned_supervisor=` from an old link is dropped before the URL is read — so
     it never reaches the state, the request or a chip, and the URL rewritten from the state loses
     it. The column stays. Every other role keeps the filter (as `orders/List.js`). */
  const supervisorShown = useSelector(state => state.me?.role) !== "supervisor";
  const urlSearch = supervisorShown ? location.search : withoutParam(location.search, "assigned_supervisor");
  /* Read once. After mount this component owns the state and writes the URL, not the reverse. */
  const initial = useRef(searchToState(urlSearch)).current;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initial.page);
  const [size, setSize] = useState(initial.size);
  /* One `loading` flag over a table that never unmounts, and `error` only changes the empty
     state — the same shape every other list keeps (see `orders/List.js`). */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const [filters, setFilters] = useState(initial.filters);
  /* Typing is separate from the applied term — 250ms debounce, as every search box in the app. */
  const [typed, setTyped] = useState(initial.search);
  const [search, setSearch] = useState(initial.search);
  const [sort, setSort] = useState(initial.sort);

  /* The point block opens by itself when the URL already filters by a point, so an active filter
     is never folded away on arrival. After that it is the user's to open and close. */
  const [pointOpen, setPointOpen] = useState(() => POINT_FILTERS.some(f => initial.filters[f.key].length));
  /* Additional fields. `cfDefs` is null until the definitions arrive; until then `cf` holds the
     URL's own `cf__…` untouched (so the URL keeps them) and the table waits for them, rather than
     send what may be junk. `cfTyped` is what the from / to number boxes hold, applied after a
     pause like the search. The block opens by itself when the URL arrived with any `cf__`. */
  const [cfDefs, setCfDefs] = useState(null);
  const [cf, setCf] = useState(initial.cfRaw);
  const [cfTyped, setCfTyped] = useState({});
  const [cfOpen, setCfOpen] = useState(() => hasCf(initial.cfRaw));
  const cfReady = cfDefs !== null;
  /* Presentation only — not a filter, so it stays out of the URL (same as `orders/List.js`). */
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN);

  const narrow = fn => (...args) => {
    setPage(1);
    fn(...args);
  };

  const setFilter = narrow((key, value) =>
    setFilters(prev => {
      const next = { ...prev, [key]: value || [] };
      /* A locality chosen under the old county may not exist in the new one — clear it (points). */
      if (key === "county") next.locality = [];
      return next;
    })
  );

  /* The lifecycle `Segmented` is a VIEW of the list, never a second state (`orders/List.js`'s
     shape): no value reads as "All", one as that segment, and several — reachable only through a
     link, since a click always leaves exactly one — as a value no option matches, which antd
     renders with nothing highlighted. The request still carries the whole list. */
  const stages = filters.lifecycle_status;
  const segment = stages.length === 0 ? "all" : stages.length === 1 ? stages[0] : SEGMENT_MIXED;
  const setSegment = value => setFilter("lifecycle_status", value === "all" ? [] : [value]);

  /* ------------------------------------------------------- additional fields */

  useEffect(() => {
    const controller = new AbortController();
    getAbortable("excalibur/counterparty-fields/?is_filterable=true", controller.signal)
      .then(response => {
        const defs = (Array.isArray(response) ? response : []).filter(f => FILTERABLE.includes(f.type));
        const clean = sanitizeCf(defs, initial.cfRaw);
        setCf(clean);
        setCfTyped(
          Object.fromEntries(
            Object.entries(clean)
              .filter(([, v]) => "gte" in v || "lte" in v)
              .map(([code, v]) => [code, { gte: v.gte != null ? Number(v.gte) : null, lte: v.lte != null ? Number(v.lte) : null }])
          )
        );
        setCfDefs(defs);
      })
      /* No definitions, no block — and none of the URL's `cf__` is sent: the table still works. */
      .catch(err => {
        if (err.name === "AbortError") return;
        setCf({});
        setCfDefs([]);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCfField = narrow((code, entry) =>
    setCf(prev => {
      const next = { ...prev };
      if (entry) next[code] = entry;
      else delete next[code];
      return next;
    })
  );
  const onCfBool = (code, value) => setCfField(code, value ? { eq: value } : null);
  const onCfChoice = (code, ids) => setCfField(code, ids && ids.length ? { eq: ids.join(",") } : null);
  const onCfNumber = (code, bound, value) => setCfTyped(prev => ({ ...prev, [code]: { ...(prev[code] || {}), [bound]: value === undefined ? null : value } }));

  /* The number boxes apply after a pause, as the search does; a "from" above "to" applies nothing. */
  useEffect(() => {
    if (!cfReady) return undefined;
    const timer = setTimeout(() => {
      const numberCodes = (cfDefs || []).filter(f => f.type === "number").map(f => f.code);
      const want = {};
      numberCodes.forEach(code => {
        const { gte = null, lte = null } = cfTyped[code] || {};
        if (gte != null && lte != null && Number(gte) > Number(lte)) return;
        if (gte != null || lte != null) want[code] = { ...(gte != null ? { gte: String(gte) } : {}), ...(lte != null ? { lte: String(lte) } : {}) };
      });
      const same = numberCodes.every(code => JSON.stringify(want[code] || null) === JSON.stringify(cf[code] || null));
      if (same) return;
      setPage(1);
      setCf(prev => {
        const next = { ...prev };
        numberCodes.forEach(code => {
          if (want[code]) next[code] = want[code];
          else delete next[code];
        });
        return next;
      });
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfTyped, cfReady]);

  const resetFilters = narrow(() => {
    setCf({});
    setCfTyped({});
    setFilters(emptyFilters());
    setTyped("");
    setSearch("");
  });

  /* ------------------------------------------------------------------ chips */

  /* Persisted: a chip restored from the URL after Back would otherwise show its raw id until the
     select re-resolves it (PICSELL-1615 #12). */
  const { labels, remember, nameFor } = useLabelCache("counterparties:labels");

  const clearChip = narrow((key, id) => {
    if (key.startsWith("cf:")) {
      setCf(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setCfTyped(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }
    if (key === "search") {
      setTyped("");
      setSearch("");
      return;
    }
    setFilters(prev => {
      const next = { ...prev, [key]: prev[key].filter(v => String(idOf(v)) !== String(id)) };
      if (key === "county") next.locality = [];
      return next;
    });
  });

  const chips = useMemo(() => {
    const out = [];
    FILTERS.forEach(({ key, label, point }) =>
      filters[key].forEach(v => out.push({ key, id: idOf(v), label: point ? `${t("point_chip_prefix")} · ${t(label)}` : t(label), value: nameFor(key, v) }))
    );
    /* A status is a fixed key with a translated label, not an entity to resolve — the chip shape
       `points/List.js` gives its own static filters. */
    STATUS_FILTERS.forEach(({ key, label, labelOf, minChips }) => {
      if (filters[key].length < minChips) return;
      filters[key].forEach(value => out.push({ key, id: value, label: t(label), value: t(labelOf(value)) }));
    });
    /* Additional fields — prefixed as the point block's chips are. */
    if (cfReady) out.push(...cfChips(cfDefs, cf, lang, t("cf_chip_prefix")));
    if (search) out.push({ key: "search", label: tp("search"), value: search });
    return out;
    /* `labels` is a dependency: a chip that arrived as an id re-renders once its name lands. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, search, labels, lang, cf, cfDefs]);

  useEffect(() => {
    if (typed === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(typed);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [typed, search]);

  /* `replace`, not `push`: Back leaves the page rather than undoing one filter at a time. */
  useEffect(() => {
    const next = stateToSearch({ filters, search, sort, page, size, cf });
    if (next !== location.search.replace(/^\?/, "")) history.replace({ search: next });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, search, sort, page, size, cf]);

  /* ------------------------------------------------------------------ fetch */

  const query = useMemo(() => buildQuery({ filters, search, sort, page, size, cf: cfReady ? cf : {} }), [filters, search, sort, page, size, cf, cfReady]);
  /* A URL that arrived with `cf__…` waits for the definitions, so nothing unchecked is ever sent. */
  const waiting = !cfReady && hasCf(initial.cfRaw);

  /* `getAbortable`, not `call`: a newer query aborts the one in flight, so a fast typist's older
     page can never land on top of the current one. */
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    if (waiting) return () => controller.abort();
    getAbortable(`excalibur/counterparties/?${query}`, controller.signal)
      .then(response => {
        if (!response) return; // 401/403 — `getAbortable` has already sent the user to /login
        setRows(response.results || []);
        setTotal(response.count || 0);
        setError(false);
        setLoading(false);
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        /* A page past the end (a stale link, a list that shrank) is DRF's 404 — a Retry could
           never succeed, so go to the first page instead. */
        if (err.message === "HTTP 404" && page > 1) {
          setPage(1);
          return;
        }
        setRows([]);
        setTotal(0);
        setError(true);
        setLoading(false);
      });
    return () => controller.abort();
  }, [query, reloadKey, waiting, page]);

  const openCard = row => history.push(`/sales/counterparties/${row.id}`, { from: location.search });

  const scrollY = useTableScrollY();
  /* Paging is the footer below, not the table's own pagination, so antd's built-in scroll-to-top on a
     page change never fires — the body is reset here by hand. */
  const tableRef = useRef(null);
  const scrollToTop = () => tableRef.current?.scrollTo({ top: 0 });
  /* Below the shell's 900px breakpoint the name column is NOT pinned. Pinned, its 240px plus the
     row menu left a ~40px window at 390px to scroll the other columns through — they were
     there, and unreachable in practice. spec/02 asks for the identity column pinned; it does not
     cover a viewport narrower than that column's own width. */
  const wide = useIsWide();

  const columns = [
    {
      /* The picture is its OWN narrow, untitled leading column — the slot orders/returns give their
         status icon (`statusColumn`: 44px, centred, pinned). Inside the name column it put the
         "Назва" header over the picture instead of over the names, 40px left of the text it
         labels. Square, like a point's network logo — a counterparty is a business, not a person.
         Initials with the photo preferred, never an `icon`: antd falls back to the children when
         the URL 404s, and an `icon` would short-circuit them (see `PersonCell`). 48px, not 44:
         the 30px picture plus antd's 8px cell padding each side. */
      key: "logo",
      title: "",
      width: 48,
      align: "center",
      fixed: wide ? "left" : undefined,
      render: (value, row) => (
        <Avatar shape="square" size={30} className="cp-logo" style={{ fontSize: 12 }} src={row.photo_url || null}>
          {initialsOf(row.name)}
        </Avatar>
      ),
    },
    {
      key: "name",
      title: t("k_name"),
      /* 306px on a wide screen: the 116px the activity column left went here rather than spread
         over every column — names are what gets cut, the other heads and pills already fit (and the
         sum is 1024px again, see `DEFAULT_HIDDEN`). Below 900px the column is not pinned, and 190px
         keeps a second column in view at 390. */
      width: wide ? 306 : 190,
      fixed: wide ? "left" : undefined,
      sorter: true,
      /* Controlled, as in `orders/List.js`: a page opened from a shared URL has to show the arrow
         for the sort it already applied. Both sortable columns carry it, which antd's controlled
         mode needs — one column's `sortOrder` without the other's leaves them disagreeing. */
      sortOrder: sort?.key === "name" ? sort.dir : undefined,
      /* The name, and under it the address in full — it wraps rather than ellipsizes (spec/02 Table
         contract), which is why this column sets no `ellipsis`. */
      render: (value, row) => {
        const name = textOf(row.name);
        return (
          <Flex vertical className="lp-main">
            <Typography.Text strong ellipsis={{ tooltip: name }}>
              {name}
            </Typography.Text>
            <Typography.Text type="secondary" className="lp-main__sub">
              {textOf(row.address)}
            </Typography.Text>
          </Flex>
        );
      },
    },
    {
      key: "client_type",
      title: t("col_type"),
      /* 114 / 130 / 112 on type / supervisor / balance, not the rounder 120 / 144 / 120: the 28px
         went to the category column so its head can be a whole word (see there). All three still
         clear their own heads in every locale, and what no longer fits ellipsizes with a tooltip. */
      width: 114,
      ellipsis: true,
      render: (value, row) => <Typography.Text ellipsis={{ tooltip: row.client_type?.label }}>{textOf(row.client_type?.label)}</Typography.Text>,
    },
    {
      /* A compact neutral tag: a category is usually one letter (A / B / C), but it is a company's
         own dictionary value, so a longer one ("Gold") ellipsizes inside the tag with the whole
         label on hover. 100px is the HEAD's width, not the tag's: measured on one line, "Category"
         is 82–100px across the four locales (Armenian the widest). Its `title` is the full name. */
      key: "client_category",
      title: <span title={t("k_client_category")}>{t("col_category")}</span>,
      width: 100,
      render: (value, row) =>
        row.client_category?.label ? (
          <Tag className="cp-cat">
            <Typography.Text ellipsis={{ tooltip: row.client_category.label }}>{row.client_category.label}</Typography.Text>
          </Tag>
        ) : (
          dash
        ),
    },
    {
      /* The card's own filled pills (`./status`), as labelled words rather than a glyph — a stage
         with four values does not reduce to one icon (the `/task` list's status column is the
         precedent for a word). A stage a person set carries the manual icon beside the pill,
         named and explained in its tooltip. 158px, not the 130 the Ukrainian head needs: measured,
         the Georgian head ("სასიცოცხლო ციკლი") is 157px on one line and the widest pill there
         122px + padding. */
      key: "lifecycle_status",
      title: t("k_lifecycle"),
      width: 158,
      render: (value, row) => lifecycleTags(row, lang) || dash,
    },
    {
      key: "supervisor",
      title: t("col_supervisor"),
      width: 130,
      render: (value, row) => <PersonCell user={row.assigned_supervisor} strong={false} />,
    },
    {
      key: "department",
      title: t("k_department"),
      width: 114,
      ellipsis: true,
      render: (value, row) => <Typography.Text ellipsis={{ tooltip: row.department?.name }}>{textOf(row.department?.name)}</Typography.Text>,
    },
    {
      key: "address",
      title: t("k_address"),
      width: 220,
      ellipsis: true,
      render: (value, row) => <Typography.Text ellipsis={{ tooltip: row.address }}>{textOf(row.address)}</Typography.Text>,
    },
    {
      key: "merchandisers",
      title: t("k_responsible"),
      width: 190,
      render: (value, row) => <MerchandisersCell people={row.merchandisers} />,
    },
    {
      key: "remaining_funds",
      /* The short head; the Columns panel and the card say "Remaining funds" in full. */
      title: t("col_remaining"),
      width: 112,
      align: "right",
      sorter: true,
      sortOrder: sort?.key === "remaining_funds" ? sort.dir : undefined,
      render: (value, row) => money(row.remaining_funds),
    },
    actionsColumn((value, row) => (
      <RowActions
        label={t("row_actions")}
        /* "Open page" first — the keyboard's way to the card, as `/sales/orders`' row menu has it
           (the row click is mouse-only). Then the two sales lists this counterparty appears in — the
           same links as the counterparty card's button on an order's / a return's page
           (`CounterpartySection.js`). Both lists read a lone `?counterparty=` as "all time", not
           their default period; the name rides in route state so their chip and field are labelled
           without a lookup. The id is a UUID, safe unescaped in a query string. The glyphs are
           those lists' own nav glyphs. */
        items={[
          { key: "open", icon: <ExportOutlined />, label: translate(ORDERS_PAGE, "open_page_btn", lang) },
          { key: "orders", icon: <ShoppingCartOutlined />, label: t("all_orders_btn") },
          { key: "returns", icon: <RollbackOutlined />, label: t("all_returns_btn") },
        ]}
        onClick={key =>
          key === "open"
            ? openCard(row)
            : history.push({ pathname: `/sales/${key}`, search: `?counterparty=${row.id}`, state: { counterpartyId: row.id, counterpartyName: row.name } })
        }
      />
    )),
  ].filter(column => !hidden.includes(column.key));

  const filterField = ({ key, entity, label, Icon, query }) => (
    <FilterCell key={key}>
      <Form.Item label={t(label)} htmlFor={`cl-${key}`}>
        <EntitySelect
          id={`cl-${key}`}
          entity={entity}
          isMulti
          value={filters[key]}
          prefix={<Icon />}
          /* The field's own name, not `ENTITY_DROPDOWN_SETTINGS`' shared placeholder — that one says
             "Місто" / "City" for a locality (spec/04 terminology: Locality) and a plural
             "Мерчендайзери" under a singular label. */
          placeholder={t(label)}
          /* Localities narrow to the county ONLY when exactly one is chosen. The `localities`
             endpoint filters `county` by exact match on one id (`filterset_fields = ["county"]` in
             picsell-api), so `county=1,2` would not work — with several counties, or none, the
             list is left whole rather than silently wrong. */
          extraQuery={query || (key === "locality" && filters.county.length === 1 ? { county: idOf(filters.county[0]) } : undefined)}
          onChange={value => setFilter(key, value)}
          onResolve={remember(key)}
        />
      </Form.Item>
    </FilterCell>
  );

  /* Three different empty states, per spec/02's table contract: a failed request, a filter that
     matched nothing, and a company with no counterparties at all need different words. */
  const emptyText = error ? (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Flex vertical gap={4} align="center">
          <Typography.Text strong>{t("error_title")}</Typography.Text>
          <Button size="small" onClick={() => setReloadKey(k => k + 1)}>
            {t("retry")}
          </Button>
        </Flex>
      }
    />
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <Flex vertical gap={4} align="center">
          <Typography.Text strong>{t(chips.length ? "empty_filtered_title" : "empty_title")}</Typography.Text>
          <Typography.Text type="secondary">{t(chips.length ? "empty_filtered_desc" : "empty_desc")}</Typography.Text>
        </Flex>
      }
    />
  );

  return (
    <Flex vertical gap="middle" className="shell-content lp">
      <Flex className="lp-head" justify="space-between" align="flex-end" gap="middle" wrap>
        <Flex vertical>
          <Typography.Title level={1} className="lp-title">
            {t("list_title")}
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="lp-sub">
            {t("page_sub")}
          </Typography.Paragraph>
        </Flex>
      </Flex>

      <Card className="lp-filters" variant="outlined">
        {/* `component={false}`: filters apply as they change, and a real <form> would turn Enter in
            the search box into a page reload. */}
        <Form layout="vertical" component={false} colon={false}>
          <FilterGrid>
            <FilterCell>
              {/* The placeholder is short because the field is; what the search actually matches
                  is stated in full under it (`Form.Item extra`, as `/profile` does its hints). */}
              <Form.Item label={tp("search")} extra={t("search_hint")} className="cp-search" htmlFor="cl-search">
                <Input
                  id="cl-search"
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t("search_placeholder")}
                  value={typed}
                  onChange={event => setTyped(event.target.value)}
                />
              </Form.Item>
            </FilterCell>
            {OWN_FILTERS.filter(f => supervisorShown || f.key !== "assigned_supervisor").map(filterField)}
          </FilterGrid>

          <Collapse
            className="cp-pointfilters"
            size="small"
            activeKey={pointOpen ? ["points"] : []}
            onChange={keys => setPointOpen(keys.length > 0)}
            items={[
              {
                key: "points",
                label: (
                  <Flex gap={8} wrap align="baseline">
                    <Typography.Text strong>{t("point_filters_title")}</Typography.Text>
                    <Typography.Text type="secondary">{POINT_FILTERS.map(f => t(f.label)).join(" · ")}</Typography.Text>
                    {/* Pushed to the far end of the header; on a narrow card it wraps under the
                        list of fields instead. Upright, not italic: spec/01 defines no italic and
                        nothing in the app uses one. */}
                    <Typography.Text type="secondary" className="cp-pointfilters__note">
                      {t("point_filters_note")}
                    </Typography.Text>
                  </Flex>
                ),
                /* Rendered while folded: a select restored from the URL has to mount to resolve
                   its ids into names, or its chips would show raw ids until the block is opened. */
                forceRender: true,
                children: (
                  <Flex vertical gap={12}>
                    <Typography.Text type="secondary">{t("point_filters_hint")}</Typography.Text>
                    <FilterGrid>{POINT_FILTERS.map(filterField)}</FilterGrid>
                  </Flex>
                ),
              },
            ]}
          />

          {cfDefs && cfDefs.length ? (
            <Collapse
              className="cp-pointfilters cp-cffilters"
              size="small"
              activeKey={cfOpen ? ["cf"] : []}
              onChange={keys => setCfOpen(keys.length > 0)}
              items={[
                {
                  key: "cf",
                  label: (
                    <Flex gap={8} wrap align="baseline">
                      <Typography.Text strong>{t("cf_filters_title")}</Typography.Text>
                      <CfHeaderNames defs={cfDefs} />
                    </Flex>
                  ),
                  children: (
                    <Flex vertical gap={12}>
                      <Typography.Text type="secondary">{t("cf_filters_hint")}</Typography.Text>
                      <FilterGrid>
                        <CustomFieldControls
                          defs={cfDefs}
                          cf={cf}
                          typed={cfTyped}
                          lang={lang}
                          colProps={CF_COL}
                          onBool={onCfBool}
                          onChoice={onCfChoice}
                          onNumber={onCfNumber}
                        />
                      </FilterGrid>
                    </Flex>
                  ),
                },
              ]}
            />
          ) : null}

          <FilterChips className="lp-chips" items={chips} closeLabel={tp("a_remove_filter")} onClose={clearChip} />

          <Flex className="lp-filters__foot" align="center" gap="small" wrap>
            <Segmented
              aria-label={t("k_lifecycle")}
              value={segment}
              onChange={setSegment}
              options={[{ value: "all", label: t("seg_all") }, ...LIFECYCLE_STATUSES.map(value => ({ value, label: t(`lc_${value}`) }))]}
            />
            <span className="lp-spacer" />
            <Button size="small" onClick={resetFilters}>
              {t("filter_reset")}
            </Button>
          </Flex>
        </Form>
      </Card>

      <Card className="lp-table" variant="outlined">
        <Flex className="lp-table__bar" align="center" gap="small" wrap>
          <Typography.Text strong className="lp-table__title">
            {t("list_title")}
          </Typography.Text>
          <Typography.Text type="secondary" aria-live="polite">
            {t("records_count")}: <Typography.Text strong>{total}</Typography.Text>
          </Typography.Text>
          <span className="lp-spacer" />
          {/* `orders/List.js`'s panel: a plain checkbox list under a `Dropdown`, not a `menu` — a
              menu item swallows the checkbox's click and closes the panel after every toggle. */}
          <Dropdown
            trigger={["click"]}
            popupRender={() => (
              <Card className="lp-colcfg" variant="outlined" size="small">
                <Checkbox.Group
                  value={TOGGLEABLE.filter(key => !hidden.includes(key))}
                  onChange={visible => setHidden(TOGGLEABLE.filter(key => !visible.includes(key)))}>
                  <Flex vertical gap={4}>
                    {TOGGLEABLE.map(key => (
                      <Checkbox key={key} value={key}>
                        {t(COLUMN_LABEL[key])}
                      </Checkbox>
                    ))}
                  </Flex>
                </Checkbox.Group>
              </Card>
            )}>
            <Button size="small" icon={<TableOutlined />}>
              {t("columns")}
            </Button>
          </Dropdown>
        </Flex>

        <Table
          ref={tableRef}
          className="lp-table__grid"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          loading={loading}
          /* The sum of the columns actually shown. antd's `width` is a MINIMUM, so the widths are
             sized for the narrowest common desktop and wider screens stretch them. ⚠ The budget at
             1366px is 1026px, not the card's 1058: measured, the page's own vertical scrollbar
             takes 15px of the card and the table body's another 15px of the header (1100px at
             1440). The default columns add up to 1024. Showing a hidden column through Columns
             grows the scroll width with it, by design. */
          scroll={{ x: columns.reduce((sum, column) => sum + (column.width || 0), 0), y: scrollY }}
          /* Paging is the footer below, so the table's own onChange only ever carries the sorter. A
             new sort starts from page 1 — page 3 of a different order is a different set of rows. */
          onChange={(pagination, tableFilters, sorter) => {
            const next = sorter?.order && ORDERING[sorter.columnKey] ? { key: sorter.columnKey, dir: sorter.order } : null;
            if (JSON.stringify(next) === JSON.stringify(sort)) return;
            setSort(next);
            setPage(1);
          }}
          /* The whole row opens the counterparty card, carrying the list's query string in route
             state so its breadcrumb can return to the exact filtered view. The `.ant-dropdown,
             .ant-btn` guard is `orders/List.js`'s: the row-action `⋮` menu must not also navigate. */
          rowClassName={() => "cp-rowlink"}
          onRow={row => ({
            onClick: event => {
              if (event.target.closest(".ant-dropdown, .ant-btn")) return;
              openCard(row);
            },
          })}
          pagination={false}
          locale={{ emptyText }}
        />
        <TableFoot
          id="cp"
          page={page}
          size={size}
          total={total}
          sizes={PAGE_SIZES}
          labels={{ perPage: t("rows_per_page"), range: t("range_of") }}
          onPage={next => {
            setPage(next);
            scrollToTop();
          }}
          onSize={next => {
            setSize(next);
            setPage(1);
            scrollToTop();
          }}
        />
      </Card>
    </Flex>
  );
};

export default List;
